import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { vdjColor, vdjBarChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_MIRROR_CHUNK, VDJ_MIRROR_NAMES } from './lib/vdjLook.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. L'océan a sa caméra et sa cible.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'wavefield',
  family: 'geometric',
  danceability: 0.50,
  energyLevel: 0.84,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.45,
  prefers: { lowEnd: [0.45, 1.0] },
  enabled: true
};

// Wavefield : un vol en rase-mottes au-dessus d'un océan en fil de fer néon
// qui EST le spectrogramme de la musique. LOOK VDJ, preset LOUNGE (dansabilité
// 0,50 sur demande, R9 0,30-0,55 : aucun flash, plan changé toutes les deux
// phrases, séparation RGB sur la mesure, drop en montée de +25 %). Techno,
// trance, progressive, bass music, synthwave.
//
// L'HISTORIQUE : une texture B bandes x R rangées (64 x 128 ; 32 x 64 au
// tier 0) tenue en ANNEAU : à chaque pas (un huitième de temps, un seizième
// dans la montée : 128 rangées = 4 mesures), la rangée du spectre courant - B valeurs reconstruites des
// trois enveloppes comme EqRig (gaussiennes, décorrélation, attaque
// instantanée, chute linéaire) - est écrite à la tête de l'anneau par
// copyTextureToTexture depuis une texture B x 1 : UNE rangée envoyée par pas,
// rien d'autre (R15 : pas de recopie de l'historique, l'anneau le décale par
// son index de tête). Le gain (montée, drop) est appliqué à l'écriture : la
// vague du drop naît au loin et arrive.
//
// LA GRILLE : B x R sommets ; la rangée r est l'âge r (0 le présent, au loin ;
// R-1 le passé, sous la caméra), posée à z = zLoin + (r + phase) x dz : elle
// avance vers la caméra pendant le pas, et au pas suivant chaque rangée lit la
// rangée de l'anneau d'un âge plus vieux - le relief défile en continu. Le
// vertex shader lit sa hauteur à la texel EXACTE de sa rangée (filtre linéaire
// pour les bandes seulement). Colonnes en miroir : graves au centre, aigus aux
// deux bords.
//
// LE RENDU : faces noires opaques (elles masquent les lignes derrière les
// crêtes), lignes par distance en pixels aux coordonnées entières de la grille
// (R1), transversales (le spectre d'un instant) plus lumineuses ; une ligne
// dont la maille fait moins de quelques pixels se fond dans sa couverture
// moyenne avant de pouvoir moirer ; brouillard noir. Ciel noir, un anneau de
// néon (soleil) à l'horizon, d'où naissent les rangées.
//
// LA CAMÉRA : RASANT, HAUT (plongée de 35°), LATÉRAL, coupés à la mesure.
// Elle suit la hauteur moyenne lissée du passé immédiat et ne descend jamais
// sous la plus haute crête qui passera sous elle dans la demi-seconde (CPU :
// une copie de l'anneau, le même calcul de hauteur que le GPU).
//
// LE SIGNAL DE TEST (?wavetest=1, ?wavebpm=<n>, 120 par défaut) : à la place
// des bandes, et sur sa propre horloge - un kick sur chaque temps (graves
// pleins 80 ms), une basse qui change de note à chaque mesure, des charleys
// sur les croches (aigus), une montée puis un drop toutes les 4 phrases.
// Sans signal (pas de bandes, pas de temps : musique arrêtée), l'océan est
// PLAT : hauteur = gain x niveau, niveau 0 -> hauteur 0 pour toutes les
// colonnes. Sans analyse mais avec une grille de temps, seul le kick du temps
// soulève les graves.
//
// Debug : ?waveshot=low|high|side, ?wavegain=<f>, ?wavehistory=1, ?wavetest=1, ?wavebpm=<n>.

const TIER = {
  0: { b: 32, e: 20, r: 64, samples: 0 },
  1: { b: 64, e: 28, r: 128, samples: 0 },
  2: { b: 64, e: 28, r: 128, samples: 4 }
};
const WIDTH = 24;            // the spectrum's width (x): the B bands, mirrored (12 made its
                             // columns 5 px apart from the high shot: their lines faded out)
// THE SEA ON THE WHOLE WIDTH: E extra columns on each side, each wider than
// the last by a constant factor, out to +-HALF_TOTAL - at least 1.6 x the
// width the widest shot sees at the horizon. They carry the treble on,
// attenuated toward the edges, so the sea goes on without a break.
const HALF_TOTAL = 100;
// THE SCREEN LAYOUT (fractions of the screen height from the top): the
// horizon - the grid's far edge, where the rows are born - and the sun, a
// whole ring above it, framing a centred cover; its top stays clear of the
// station name's band (10 %), here at 24 %.
const HORIZON_FROM_TOP = 0.64;
const SUN_FROM_TOP = 0.43;
const SUN_R = 0.19;
const DEPTH = 44;            // the grid's depth (z)
const Z_NEAR = 3;            // the oldest row (under the camera)
// height at level 1, gain 1: a kick's crest rises to 8 % of the screen
// height in the foreground of the low shot (measured with ?wavetest=1)
const H_MAX = 0.36;
const STORE = 2.0;           // the texture stores h / (H_MAX * STORE): gain up to 2
const CAM_CLEAR = 0.30;
const LOOKAHEAD_S = 0.6;
const SHOTS = ['low', 'high', 'side'];
const SHOT_CYCLE = ['low', 'low', 'high', 'low', 'side', 'low', 'high', 'side'];
const MIRROR_EVERY_PHRASES = 3;
// the spectrum (EqRig's)
const BAND_CENTRES = [0.12, 0.50, 0.88];
const BAND_SIGMA = 0.26;
const DECORR = 0.12;
const FALL_PER_S = 1.8;
const KICK_SPIKE = 0.85;          // the beat's kick in the bass, when there is no analysis
const SYNTH_BPM = 128;
const PALETTES = [[0x2bd6ff, 0xff2bd6], [0xff7a1f, 0x8a2bff], [0x2bff9e, 0xff2b5e], [0xffd21f, 0x2b6bff]];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smoothstepJs(a, b, x) { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const gridVertex = `
  precision highp float;
  attribute vec2 aCR;             // column 0..C-1, row (age) 0..R-1
  attribute vec3 aXB;             // x, band (0 bass .. 1 treble), attenuation
  uniform sampler2D uHist;
  uniform float uB;
  uniform float uR;
  uniform float uHead;            // the ring row of age 0
  uniform float uPhase;           // the step's progress, 0..1
  uniform float uDz;
  uniform float uZFar;
  uniform float uWidth;
  uniform float uHScale;          // texture value -> height
  varying vec2 vG;                // grid coordinates (lines at integers)
  varying float vH;               // height 0..1 of the gain-1 maximum
  varying vec3 vW;
  varying float vAge;
  void main() {
    float c = aCR.x, r = aCR.y;
    // mirrored columns: the bass in the middle, the treble at both edges of
    // the spectrum, then carried on to the sea's edges, attenuated
    float u = (aXB.y * (uB - 1.0) + 0.5) / uB;
    float row = mod(uHead - r + uR, uR);
    float v = (row + 0.5) / uR;
    float h = texture2D(uHist, vec2(u, v)).r * uHScale * aXB.z;
    vec3 p = vec3(aXB.x, h, uZFar + (r + uPhase) * uDz);
    vG = vec2(c, r);
    vH = h / ${H_MAX.toFixed(3)};
    vW = p;
    vAge = r + uPhase;
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;
const gridFragment = `
  precision highp float;
  uniform vec3 uLow;              // the troughs: a dark palette colour
  uniform vec3 uAccent;           // the slopes: toward the saturated accent
  uniform float uR;
  uniform float uRowFlash;
  uniform float uPulse;
  uniform vec2 uSunPx;            // the sun's centre, px (gl_FragCoord space)
  uniform float uSunR;            // its radius, px
  uniform float uHorizonY;        // the horizon's height, px from the bottom
  uniform float uHalfW;
  uniform float uResY;
  varying vec2 vG;
  varying float vH;
  varying vec3 vW;
  varying float vAge;
  // px from the nearest integer of g, and the cell's size in px
  vec2 lineDist(float g) {
    float fw = max(fwidth(g), 1e-5);
    return vec2(abs(fract(g + 0.5) - 0.5) / fw, 1.0 / fw);
  }
  void main() {
    float hn = max(vH, 0.0);                                  // 1 = a gain-1 level-1 crest
    vec3 base = mix(uLow, uAccent, smoothstep(0.0, 0.7, hn)); // trough -> slope
    float crest = smoothstep(0.62, 0.78, hn);                 // above 70 % of the maximum
    // the transverse lines (the spectrum of one instant) at 100 %, the
    // longitudinal ones at 45 %; each fades to ZERO once two lines are less
    // 5 px apart - gone by 2.5 px (R1: no solid band, no haze, at the horizon)
    vec2 t = lineDist(vG.y), l = lineDist(vG.x);
    float tFade = smoothstep(2.5, 5.0, t.y), lFade = smoothstep(2.5, 5.0, l.y);
    float tSheath = (1.0 - smoothstep(1.2, 2.2, t.x)) * tFade;
    float lSheath = (1.0 - smoothstep(1.0, 1.9, l.x)) * lFade;
    float tCore = (1.0 - smoothstep(0.25, 1.25, t.x)) * tFade * crest;   // 1.5 px white core
    float lCore = (1.0 - smoothstep(0.25, 1.25, l.x)) * lFade * crest;
    // the present (the farthest rows) the brightest, flashing on the beat
    float present = 1.0 - smoothstep(0.0, 3.0, vAge);
    float tGain = 1.0 + present * (0.6 + 0.8 * uRowFlash) + 0.08 * uPulse;
    vec3 col = base * tSheath * tGain + base * lSheath * 0.45;
    col = mix(col, vec3(1.0), max(tCore, lCore * 0.45));      // white, over the bloom threshold
    // black fog over the farthest 20 % of the grid (not to zero: the present
    // row, which is born there, stays the brightest)
    float depth = vAge / (uR - 1.0);                          // 0 far .. 1 near
    col *= mix(0.45, 1.0, smoothstep(0.0, 0.2, depth)) * min(1.0, 0.6 + present * 2.0);
    // the sun's REFLECTION, just under the horizon: the ring mirrored about
    // the horizon and squashed (x 0.3), blurred, pushed up by the relief -
    // and seen only on the lines (the faces stay black)
    float below = uHorizonY - gl_FragCoord.y;                 // px under the horizon
    if (below > 0.0) {
      float yr = uHorizonY + (below - vH * 0.035 * uResY) / 0.30;
      float dr = abs(length(vec2(gl_FragCoord.x - uSunPx.x, yr - uSunPx.y)) - uSunR);
      float refl = exp(-dr / (0.012 * uResY)) * (1.0 - smoothstep(0.0, 0.22 * uResY, below));
      col += uAccent * refl * 0.55 * (tSheath + 0.6 * lSheath) * (1.0 + 0.08 * uPulse);
    }
    // the sea's own edges, far out at the sides, fade to black
    col *= 1.0 - smoothstep(0.80, 1.0, abs(vW.x) / uHalfW);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// the sun: a whole neon ring in SCREEN space (its place is a layout, not a
// world position: the same spot in all three shots), drawn first behind the
// grid; R1 strokes in px: white core, accent sheath, halo under the bloom
// threshold; on the beat +8 % luminance, +2 % radius
const sunVertex = `
  void main() { gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const sunFragment = `
  precision highp float;
  uniform vec3 uAccent;
  uniform float uSunPulse;
  uniform vec2 uSunPx;
  uniform float uSunR;
  uniform float uResY;
  void main() {
    float R = uSunR * (1.0 + 0.02 * uSunPulse);
    float d = abs(length(gl_FragCoord.xy - uSunPx) - R);     // px to the ring
    float core = 1.0 - smoothstep(0.5, 1.3, d);
    float sheath = 1.0 - smoothstep(2.2, 4.2, d);
    float halo = exp(-d / (0.018 * uResY)) * 0.30;
    vec3 col = mix(uAccent * (0.72 * sheath + halo), vec3(1.0), core) * (1.0 + 0.08 * uSunPulse);
    gl_FragColor = vec4(min(col, vec3(1.0)), 1.0);
  }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const quadFragment = `
  precision highp float;
  uniform sampler2D uScene;
  uniform sampler2D uHist;
  uniform float uShowHist;
  uniform float uHead;
  uniform float uR;
  uniform float uMirror;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  ${VDJ_MIRROR_CHUNK}
  void main() {
    vec2 uv = vdjMirror(vUv, uMirror, uAspect);
    vec3 col = texture2D(uScene, uv).rgb * uGain;
    // the cover's safe zone, per fragment (R19: it follows the cover)
    vec2 s = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));
    // ?wavehistory=1: the ring, newest row at the top, bottom-left corner
    if (uShowHist > 0.5) {
      vec2 a = (vUv - vec2(0.02, 0.05)) / vec2(0.22, 0.40);
      if (a.x >= 0.0 && a.x <= 1.0 && a.y >= 0.0 && a.y <= 1.0) {
        float age = floor((1.0 - a.y) * uR);
        float row = mod(uHead - age + uR, uR);
        col = vec3(texture2D(uHist, vec2(a.x, (row + 0.5) / uR)).r);
      }
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;

// the growth factor that carries E columns from the spectrum's edge (step s0)
// out to HALF_TOTAL
function growthFor(E, s0, reach) {
  let lo = 1.0001, hi = 2.0;
  for (let i = 0; i < 60; i++) {
    const g = 0.5 * (lo + hi);
    const total = s0 * g * (Math.pow(g, E) - 1) / (g - 1);
    if (total > reach) hi = g; else lo = g;
  }
  return 0.5 * (lo + hi);
}
function gridGeometry(B, E, R) {
  const C = B + 2 * E;
  const s0 = WIDTH / (B - 1);
  const g = growthFor(E, s0, HALF_TOTAL - WIDTH / 2);
  // per column: x, band (0 the bass at the centre .. 1 the treble at the
  // spectrum's edge), attenuation (1 on the spectrum, falling outward)
  const colX = new Float32Array(C), colBand = new Float32Array(C), colAtt = new Float32Array(C);
  for (let c = 0; c < B; c++) {
    colX[E + c] = (c / (B - 1) - 0.5) * WIDTH;
    colBand[E + c] = Math.abs(c - (B - 1) * 0.5) / ((B - 1) * 0.5);
    colAtt[E + c] = 1;
  }
  let x = WIDTH / 2, step = s0;
  for (let k = 1; k <= E; k++) {
    step *= g; x += step;
    const att = Math.exp(-k / 7);
    colX[E + B - 1 + k] = x; colX[E - k] = -x;
    colBand[E + B - 1 + k] = colBand[E - k] = 1;
    colAtt[E + B - 1 + k] = colAtt[E - k] = att;
  }
  const n = C * R;
  const pos = new Float32Array(n * 3), cr = new Float32Array(n * 2), xb = new Float32Array(n * 3), idx = [];
  for (let r = 0; r < R; r++) for (let c = 0; c < C; c++) {
    const i = r * C + c;
    cr[i * 2] = c; cr[i * 2 + 1] = r;
    xb[i * 3] = colX[c]; xb[i * 3 + 1] = colBand[c]; xb[i * 3 + 2] = colAtt[c];
  }
  for (let r = 0; r < R - 1; r++) for (let c = 0; c < C - 1; c++) {
    const a = r * C + c, b = a + 1, d = a + C, e = d + 1;
    idx.push(a, d, b, b, d, e);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aCR', new THREE.BufferAttribute(cr, 2));
  geo.setAttribute('aXB', new THREE.BufferAttribute(xb, 3));
  geo.setIndex(idx);
  return geo;
}

export class WavefieldVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const sh = urlParam('waveshot');
    this._forcedShot = SHOTS.indexOf(sh) >= 0 ? sh : null;
    const g = parseFloat(urlParam('wavegain'));
    this._forcedGain = Number.isFinite(g) ? Math.max(0, Math.min(2, g)) : null;
    this._showHist = urlParam('wavehistory') === '1';
    this._test = urlParam('wavetest') === '1';
    const tb = parseFloat(urlParam('wavebpm'));
    this._testBpm = Number.isFinite(tb) && tb > 40 ? Math.min(220, tb) : 120;
    const mi = urlParam('wavemirror');
    this._forcedMirror = mi && VDJ_MIRROR_NAMES[mi] != null ? VDJ_MIRROR_NAMES[mi] : null;
    this._post = new VdjPost({ bloomStrength: 0.30, bloomThreshold: 0.95, vignette: 0.30, grain: 0.010, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.lounge.comfort);      // refuses every flash
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const L = this._layout, B = L.b, R = L.r;
    this._B = B; this._R = R; this._E = L.e;
    this._dz = DEPTH / (R - 1);
    this._zFar = Z_NEAR - DEPTH;
    this._inner = new THREE.Scene();
    this._cam = new THREE.PerspectiveCamera(64, 16 / 9, 0.05, 120);

    // the ring (B x R) and the row it is written from (B x 1)
    this._rowData = new Uint8Array(B * 4);
    this._rowTex = new THREE.DataTexture(this._rowData, B, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._rowTex.needsUpdate = true;
    this._hist = new THREE.DataTexture(new Uint8Array(B * R * 4), B, R, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._hist.minFilter = THREE.NearestFilter;          // rows are read at their texel centre;
    this._hist.magFilter = THREE.LinearFilter;           // the bands are filtered (8-bit: the Pi can)
    this._hist.wrapS = THREE.ClampToEdgeWrapping; this._hist.wrapT = THREE.RepeatWrapping;
    this._hist.generateMipmaps = false; this._hist.needsUpdate = true;
    this._histCpu = new Float32Array(B * R);              // the same ring, for the camera
    this._head = 0; this._queue = []; this._ringReady = false; this._uploads = 0;

    this._u = {
      uHist: { value: this._hist }, uB: { value: B }, uR: { value: R }, uHead: { value: 0 }, uPhase: { value: 0 },
      uDz: { value: this._dz }, uZFar: { value: this._zFar }, uWidth: { value: WIDTH }, uHScale: { value: H_MAX * STORE },
      uLow: { value: new THREE.Color() }, uAccent: { value: new THREE.Color() }, uSunPulse: { value: 0 },
      uRowFlash: { value: 0 }, uPulse: { value: 0 },
      uSunPx: { value: new THREE.Vector2() }, uSunR: { value: 100 }, uHorizonY: { value: 0 }, uHalfW: { value: HALF_TOTAL }, uResY: { value: 720 }
    };
    this._grid = new THREE.Mesh(gridGeometry(B, L.e, R), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: gridVertex, fragmentShader: gridFragment, side: THREE.DoubleSide }));
    this._grid.frustumCulled = false;
    this._inner.add(this._grid);
    this._sun = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: sunVertex, fragmentShader: sunFragment, depthTest: false, depthWrite: false }));
    this._sun.renderOrder = -10; this._sun.frustumCulled = false;
    this._inner.add(this._sun);

    this._rt = new THREE.WebGLRenderTarget(4, 4, { samples: L.samples, type: THREE.UnsignedByteType, colorSpace: THREE.SRGBColorSpace, depthBuffer: true, stencilBuffer: false });
    this._qu = {
      uScene: { value: this._rt.texture }, uHist: { value: this._hist }, uShowHist: { value: this._showHist ? 1 : 0 },
      uHead: { value: 0 }, uR: { value: R }, uMirror: { value: 0 }, uGain: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._qu, vertexShader: quadVertex, fragmentShader: quadFragment, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2(); this._dst = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);

    // state
    let seed = 24681357;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    this._time = 0; this._beats = 0; this._steps = 0; this._phase = 0;
    this._lastBeatPhase = null; this._lastBeatIndex = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._beatPeriod = 60 / SYNTH_BPM;
    this._level = new Float32Array(B); this._target = new Float32Array(B);
    this._noisePhase = new Float32Array(B); this._noiseRate = new Float32Array(B);
    for (let b = 0; b < B; b++) { this._noisePhase[b] = rnd() * 6.28; this._noiseRate[b] = 0.5 + 1.2 * rnd(); }
    this._kick = 0; this._pulse = 0; this._rowFlash = 0;
    this._writeGain = 1; this._dropAt = -99; this._lastDropKey = null; this._stepsPerBeat = 8;
    this._palIndex = 0; this._vivid = null; this._lastPaletteRef = undefined;
    this._mirror = this._forcedMirror != null ? this._forcedMirror : 0;
    this._shotIndex = 0; this._shot = this._forcedShot || 'low';
    this._camY = 1.2; this._minClear = Infinity; this._clearance = 0;
    this._rowsWritten = 0;
    this._setColours();
  }

  // --- colours: a palette pair from the cover (or the house pairs), by phrase
  _setColours() {
    let a, b;
    const v = this._vivid;
    if (v && v.length >= 2) {
      // the accent: the cover's colour of this phrase; the low: the one furthest in hue
      a = v[this._palIndex % v.length];
      const hsl = {}; new THREE.Color(a).getHSL(hsl); const h0 = hsl.h;
      let bd = -1; b = v[(this._palIndex + 1) % v.length];
      for (const c of v) { new THREE.Color(c).getHSL(hsl); let d = Math.abs(hsl.h - h0); d = Math.min(d, 1 - d); if (d > bd) { bd = d; b = c; } }
    } else { const p = PALETTES[this._palIndex % PALETTES.length]; a = p[0]; b = p[1]; }
    vdjColor(a, this._u.uAccent.value, 1.0);
    // the troughs: the other palette colour, dark (lightness 0.25)
    const hsl = {}; new THREE.Color(b).getHSL(hsl);
    this._u.uLow.value.setHSL(hsl.h, 0.9, 0.25);
  }

  // --- the spectrum: B bands from the three envelopes (EqRig's method) ---------
  _spectrum(bands, energy, dt) {
    const B = this._B, bv = bands ? [bands.low, bands.mid, bands.high] : null;
    for (let b = 0; b < B; b++) {
      const x = b / (B - 1);
      let v;
      if (bv) {
        let ws = 0; v = 0;
        for (let i = 0; i < 3; i++) { const d = (x - BAND_CENTRES[i]) / BAND_SIGMA; const w = Math.exp(-d * d); v += w * bv[i]; ws += w; }
        v = ws > 0 ? v / ws : 0;
      } else v = 0;                     // no analysis: no spectrum (level 0, height 0)
      this._noisePhase[b] += dt * this._noiseRate[b];
      v *= 1 + DECORR * Math.sin(this._noisePhase[b]);        // multiplicative: 0 stays 0
      // the beat's kick in the bass (a beat grid is a signal too)
      v += KICK_SPIKE * this._kick * Math.exp(-x * x / 0.012);
      this._target[b] = clamp01(v);
    }
    this._kick = Math.max(0, this._kick - dt * 7);
    for (let b = 0; b < B; b++) {
      const t = this._target[b];
      let v = this._level[b];
      v = t >= v ? t : Math.max(t, v - FALL_PER_S * dt);
      this._level[b] = v;
    }
  }

  // ?wavetest=1: the synthetic spectrum at test beat position tb (beats)
  _testSpectrum(tb, dt) {
    const B = this._B;
    const ph = tb - Math.floor(tb), sinceBeatS = ph * this._beatPeriod;
    const e8 = (tb * 2) - Math.floor(tb * 2), sinceEighthS = e8 * this._beatPeriod / 2;
    const bar = Math.floor(tb / 4);
    const note = [0.10, 0.16, 0.07, 0.13][bar % 4];            // the bass line: a note per bar
    const kick = sinceBeatS < 0.08 ? 1 : 0;                     // the kick: full bass for 80 ms
    const hat = sinceEighthS < 0.04 ? 1 : 0;                    // the hats on the eighths
    for (let b = 0; b < B; b++) {
      const x = b / (B - 1);
      let v = 0.95 * kick * Math.exp(-x * x / 0.006);
      v = Math.max(v, 0.38 * Math.exp(-Math.pow((x - note) / 0.025, 2)) * (0.75 + 0.25 * Math.cos(ph * Math.PI * 2)));
      v = Math.max(v, 0.22 * hat * smoothstepJs(0.72, 0.85, x));
      this._target[b] = clamp01(v);
    }
    for (let b = 0; b < B; b++) {
      const t = this._target[b];
      let v = this._level[b];
      v = t >= v ? t : Math.max(t, v - 6.0 * dt);              // the test's fall: crisp ridges
      this._level[b] = v;
    }
  }

  // one step: the current spectrum written at the ring's head (one row)
  _writeRow() {
    const B = this._B, R = this._R;
    this._head = (this._head + 1) % R;
    const g = this._forcedGain != null ? this._forcedGain : this._writeGain;
    for (let b = 0; b < B; b++) {
      const h = this._level[b] * g;                           // height = gain x level: 0 -> 0
      const s = Math.max(0, Math.min(255, Math.round(h / STORE * 255)));
      this._rowData[b * 4] = s; this._rowData[b * 4 + 3] = 255;
      this._histCpu[this._head * B + b] = s / 255 * STORE * H_MAX;   // exactly what the GPU reads
    }
    this._queue.push({ head: this._head, data: this._rowData.slice() });
    this._rowsWritten++;
  }

  // the height the GPU draws at column c, age r (the bands interpolated like
  // the linear filter, the mirror the same)
  _heightAt(c, r) {
    const B = this._B, R = this._R;
    const m = Math.abs(c - (B - 1) * 0.5) / ((B - 1) * 0.5);
    const fb = m * (B - 1);
    const b0 = Math.floor(fb), b1 = Math.min(B - 1, b0 + 1), t = fb - b0;
    const row = ((this._head - r) % R + R) % R;
    return this._histCpu[row * B + b0] * (1 - t) + this._histCpu[row * B + b1] * t;
  }
  // the highest point over columns [c0, c1] and ages [r0, r1]
  _maxHeight(c0, c1, r0, r1) {
    const B = this._B, R = this._R;
    let m = 0;
    for (let r = Math.max(0, Math.floor(r0)); r <= Math.min(R - 1, Math.ceil(r1)); r++)
      for (let c = Math.max(0, Math.floor(c0)); c <= Math.min(B - 1, Math.ceil(c1)); c++) m = Math.max(m, this._heightAt(c, r));
    return m;
  }
  _meanHeight(c0, c1, r0, r1) {
    const B = this._B, R = this._R;
    let s = 0, n = 0;
    for (let r = Math.max(0, Math.floor(r0)); r <= Math.min(R - 1, Math.ceil(r1)); r++)
      for (let c = Math.max(0, Math.floor(c0)); c <= Math.min(B - 1, Math.ceil(c1)); c++) { s += this._heightAt(c, r); n++; }
    return n ? s / n : 0;
  }
  _xToCol(x) { return (x / WIDTH + 0.5) * (this._B - 1); }
  _zToAge(z) { return (z - this._zFar) / this._dz - this._phase; }

  // --- rhythm ---------------------------------------------------------------------
  _onBeat() { this._beatCount++; this._kick = 1; this._pulse = 1; this._rowFlash = 1; }
  _onBar() {
    this._barCount++;
    this._barAt = this._time;
  }
  _onPhrase() {
    this._phraseCount++;
    // lounge: a new shot every second phrase (R9), not every bar
    if (!this._forcedShot && this._phraseCount % 2 === 0) {
      this._shotIndex = (this._shotIndex + 1) % SHOT_CYCLE.length;
      this._shot = SHOT_CYCLE[this._shotIndex];
    }
    this._palIndex++;
    this._setColours();
    if (this._forcedMirror == null) this._mirror = this._phraseCount % MIRROR_EVERY_PHRASES === 0 ? (this._phraseCount % (2 * MIRROR_EVERY_PHRASES) === 0 ? 2 : 1) : 0;
  }
  _onDrop(director) {
    this._dropAt = this._time;
    void director;                 // lounge: no flash (R9); the drop is the swell below
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.05);
    const director = profile.director;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors;
      this._vivid = v && v.length ? v.slice(0, 5) : null;
      this._setColours();
    }
    if (paused) return;                 // the frame holds
    this._time += dt;
    const bands = director && director.hasReliableGrid && director.bands ? director.bands : null;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.65;
    const test = this._test;
    if (test) this._beatPeriod = 60 / this._testBpm;

    // the clocks (the test signal runs on its own)
    const beatPhase = test ? null : (director && director.beat != null ? director.beat : profile.beatPhase);
    let beatEdge = false;
    const before = this._beats;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true;
      const bi = profile.beatIndex;
      if (bi != null && this._lastBeatIndex != null) this._beats += Math.max(0, (bi - this._lastBeatIndex) + (beatPhase - this._lastBeatPhase));
      else this._beats += dt / this._beatPeriod;
      this._lastBeatIndex = bi != null ? bi : null;
      this._lastBeatPhase = beatPhase;
    } else {
      this._beats += dt / this._beatPeriod;
      if (Math.floor(this._beats) !== Math.floor(before)) beatEdge = true;
    }
    const dBeats = Math.max(0, this._beats - before);
    const ev = test ? null : director && director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    // the test: a drop every 4 phrases (128 beats), the montee over the 32 before
    const testToDrop = test ? 128 - (this._beats % 128) : null;
    const beatsToDrop = test ? testToDrop : (dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null);
    const montee = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 32) : 0;
    if (beatEdge) this._onBeat();
    let barPhase = test ? null : (director && director.bar != null ? director.bar : profile.barPhase);
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else if (Math.floor(this._beats / 4) !== Math.floor(before / 4)) barEdge = true;
    if (barEdge) this._onBar();
    const phrasePhase = test ? null : (director && director.phrase != null ? director.phrase : profile.phrasePhase);
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase(); this._lastPhrasePhase = phrasePhase; }
    else if (Math.floor(this._beats / 32) !== Math.floor(before / 32)) this._onPhrase();
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (test && Math.floor(this._beats / 128) !== Math.floor(before / 128) && this._beats >= 128) this._onDrop(director);
    if (!test && dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey && this._time - this._dropAt > 2) { this._lastDropKey = key; this._onDrop(director); }
    }

    // the write gain: flattened in the montee (calm before the storm), x2 for
    // four bars from the drop (the tidal wave is WRITTEN, then arrives)
    const sinceDrop = (this._time - this._dropAt) / this._beatPeriod;
    // lounge drop: +25 % for 8 bars (VDJ_PRESETS.lounge.dropRise), not x2
    this._writeGain = sinceDrop >= 0 && sinceDrop < 32 ? 1.25 : 1 - 0.75 * montee;
    // the speed: a row every eighth of a beat (the 128 rows hold 4 bars), every
    // sixteenth in the montee
    this._stepsPerBeat = montee > 0.25 ? 16 : 8;
    if (test) this._testSpectrum(this._beats, dt); else this._spectrum(bands, energy, dt);
    const s0 = Math.floor(this._steps);
    this._steps += dBeats * this._stepsPerBeat;
    let n = Math.floor(this._steps) - s0;
    n = Math.min(n, this._R);
    for (let k = 0; k < n; k++) this._writeRow();
    this._phase = this._steps - Math.floor(this._steps);

    this._pulse = Math.max(0, this._pulse - dt / (this._beatPeriod * 0.35));
    this._rowFlash = Math.max(0, this._rowFlash - dt / (this._beatPeriod * 0.5));
    this._post.update(vdjBarChroma(this._barAt != null ? this._time - this._barAt : 9));
    const u = this._u;
    u.uHead.value = this._head; u.uPhase.value = this._phase;
    // lounge: the beat only breathes (a third of the party flash)
    u.uRowFlash.value = this._rowFlash * 0.3; u.uPulse.value = this._pulse * 0.5; u.uSunPulse.value = this._pulse * 0.5;
    this._qu.uHead.value = this._head;
    this._qu.uMirror.value = this._mirror;
    const est = 0.08 + (director && director.flash ? director.flash * 0.8 : 0);
    this._qu.uGain.value = this._comfort.measure(est, dt);
    this._camStep(dt);
  }

  // --- the camera -----------------------------------------------------------------
  _camStep(dt) {
    const B = this._B, R = this._R;
    const ageSpeed = this._stepsPerBeat / this._beatPeriod;          // rows per second toward the camera
    const look = LOOKAHEAD_S * ageSpeed;
    if (this._shot === 'low' || this._shot === 'high') {
      // the camera over the grid's axis at z = Z_NEAR - 2: the rows under it,
      // and those that will be under it within LOOKAHEAD_S
      const zc = Z_NEAR - 2.0;
      const age = this._zToAge(zc);
      const c0 = this._xToCol(-1.2), c1 = this._xToCol(1.2);
      const ahead = this._maxHeight(c0, c1, age - look - 1, age + 1);
      const mean = this._meanHeight(this._xToCol(-3), this._xToCol(3), age - 4, age + 2);
      // the boat: it rides the swell UNDER it (rising with each wave), never
      // below the safety floor of the crests that will pass under it soon
      const here = this._maxHeight(c0, c1, age - 1, age + 1);
      const target = this._shot === 'low' ? Math.max(ahead + CAM_CLEAR, 0.50 + 0.9 * here + 0.4 * mean) : 0;
      // the boat: rises fast (time constant 0.15 s), settles slowly (0.6 s)
      const k = target > this._camY ? Math.min(1, dt / 0.10) : Math.min(1, dt / 0.25);
      this._camY += (target - this._camY) * k;
      // never below the crest passing under it now
      const under = this._maxHeight(c0, c1, age - 1, age + 1) + CAM_CLEAR * 0.6;
      if (this._camY < under) this._camY = under;
    }
  }

  // the camera at pos, heading along the horizontal unit vector fwd, pitched
  // so that the target (the grid's far edge, on the screen's centre line)
  // lands HORIZON_FROM_TOP down the screen - the same horizon in every shot
  _aim(pos, fwd, target, fov) {
    const cam = this._cam;
    cam.fov = fov;
    cam.position.copy(pos);
    cam.up.set(0, 1, 0);
    const dx = target.x - pos.x, dz = target.z - pos.z;
    const horiz = Math.max(1e-3, dx * fwd.x + dz * fwd.z);
    const eT = Math.atan2(target.y - pos.y, horiz);            // the target's elevation
    const ndcY = 1 - 2 * HORIZON_FROM_TOP;                       // -0.28
    const eC = eT - Math.atan(ndcY * Math.tan(fov * Math.PI / 360));
    const look = new THREE.Vector3(fwd.x * Math.cos(eC), Math.sin(eC), fwd.z * Math.cos(eC)).add(pos);
    cam.lookAt(look);
    cam.updateProjectionMatrix();
    cam.updateMatrixWorld();
  }

  _pose() {
    const zFarEdge = this._zFar;
    if (this._shot === 'low') {
      this._aim(new THREE.Vector3(0, this._camY, Z_NEAR - 2.0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 0, zFarEdge), 64);
      this._horizonTarget = new THREE.Vector3(0, 0, zFarEdge);
    } else if (this._shot === 'high') {
      // high above the near end, looking down the spectrogram
      this._aim(new THREE.Vector3(0, 24, Z_NEAR + 8), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 0, zFarEdge), 58);
      this._horizonTarget = new THREE.Vector3(0, 0, zFarEdge);
    } else {
      // side: from beside the spectrum, looking +x across the sea: the rows
      // flow left to right; the horizon is the sea's far side
      const edge = this._maxHeight(0, 6, 0, this._R - 1);
      const y = Math.max(2.6, edge + 1.1);
      const zc = this._zFar * 0.30 + Z_NEAR * 0.70;
      this._aim(new THREE.Vector3(-WIDTH * 0.5 - 6.0, y, zc), new THREE.Vector3(1, 0, 0), new THREE.Vector3(HALF_TOTAL * 0.8, 0, zc), 60);
      this._horizonTarget = new THREE.Vector3(HALF_TOTAL * 0.8, 0, zc);
    }
    // the screen layout: the horizon where the target lands, the sun above it
    const W = this._size.x, H = this._size.y, u = this._u;
    const t = this._horizonTarget.clone().project(this._cam);
    u.uHorizonY.value = (t.y * 0.5 + 0.5) * H;
    const narrow = Math.min(1, (W / Math.max(1, H)) / (16 / 9));       // narrower than 16:9: smaller
    u.uSunR.value = SUN_R * H * narrow;
    u.uSunPx.value.set(W * 0.5, H * (1 - SUN_FROM_TOP));
    u.uResY.value = H;
    this._checkClearance();
  }


  // the probe: the camera's height above the highest point within 0.8 units
  _checkClearance() {
    const p = this._cam.position;
    if (p.x < -WIDTH * 0.5 - 0.5) { this._clearance = p.y - this._maxHeight(0, 4, 0, this._R - 1); }
    else {
      const age = this._zToAge(p.z);
      const h = this._maxHeight(this._xToCol(p.x - 0.8), this._xToCol(p.x + 0.8), age - 0.8 / this._dz, age + 0.8 / this._dz);
      this._clearance = p.y - h;
    }
    if (this._clearance < this._minClear) this._minClear = this._clearance;
  }

  _render(renderer) {
    // the rows of this frame: one texSubImage2D of B x 1 each, at the ring's head
    if (!this._ringReady) { renderer.initTexture(this._hist); this._ringReady = true; }
    // (a long frame can hold several steps: each row goes to its own head)
    for (const q of this._queue) {
      this._rowTex.image.data = q.data;
      renderer.copyTextureToTexture(this._rowTex, this._hist, null, this._dst.set(0, q.head));
      this._uploads++;
    }
    this._queue.length = 0;
    renderer.getDrawingBufferSize(this._size);
    if (this._rt.width !== this._size.x || this._rt.height !== this._size.y) this._rt.setSize(this._size.x, this._size.y);
    this._cam.aspect = this._size.x / Math.max(1, this._size.y);
    this._pose();
    const prev = renderer.getRenderTarget();
    const cc = new THREE.Color(); renderer.getClearColor(cc); const ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, true, false);
    renderer.render(this._inner, this._cam);
    renderer.setClearColor(cc, ca);
    renderer.setRenderTarget(prev);
  }

  getProbe() {
    const B = this._B;
    const row0 = Array.from({ length: B }, (_, c) => +this._heightAt(c, 0).toFixed(2));
    return {
      t: +this._time.toFixed(2), beats: +this._beats.toFixed(2), steps: +this._steps.toFixed(2), head: this._head, phase: +this._phase.toFixed(3),
      shot: this._shot, camY: +this._camY.toFixed(3), clearance: +this._clearance.toFixed(3), minClear: +this._minClear.toFixed(3),
      writeGain: +this._writeGain.toFixed(2), stepsPerBeat: this._stepsPerBeat, rowsWritten: this._rowsWritten, uploads: this._uploads,
      centreByAge: Array.from({ length: this._R }, (_, r) => +this._heightAt((B - 1) / 2, r).toFixed(2)),
      row0, mirror: this._mirror, gain: +this._comfort.gain.toFixed(3)
    };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    for (const m of [this._grid, this._sun]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._rt) this._rt.dispose();
    if (this._hist) this._hist.dispose();
    if (this._rowTex) this._rowTex.dispose();
    this._scene = null;
  }
}
